import React, { useState } from 'react';
import { CheckIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function SolutionSection() {
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);
  const barData = [{
    height: 40,
    label: 'Mon',
    value: '$2.4k'
  }, {
    height: 60,
    label: 'Tue',
    value: '$3.6k'
  }, {
    height: 45,
    label: 'Wed',
    value: '$2.7k'
  }, {
    height: 90,
    label: 'Thu',
    value: '$5.4k'
  }, {
    height: 65,
    label: 'Fri',
    value: '$3.9k'
  }, {
    height: 85,
    label: 'Sat',
    value: '$5.1k'
  }, {
    height: 70,
    label: 'Sun',
    value: '$4.2k'
  }, {
    height: 95,
    label: 'Mon',
    value: '$5.7k'
  }, {
    height: 80,
    label: 'Tue',
    value: '$4.8k'
  }, {
    height: 100,
    label: 'Wed',
    value: '$6.0k'
  }];
  return <section id="how-it-works" className="bg-cool-gray py-24 md:py-32 overflow-hidden" data-id="element-218">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-20 items-center" data-id="element-219">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="space-y-8" data-id="element-220">
          <h2 className="text-midnight-navy text-4xl md:text-5xl font-manrope font-bold leading-tight" data-id="element-221">
            Operational Clarity — Without the Complexity.
          </h2>
          <p className="text-midnight-navy/70 text-lg leading-relaxed" data-id="element-222">
            Not generic accounting software. All designed around how restaurants
            actually operate. We map your data to your real-world processes.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-223">
            {['Track money in vs money out', 'Monitor food and labor costs', 'Understand operational spending', 'Improve inventory visibility', 'Make faster business decisions', 'Identify profit leaks early'].map((item, idx) => <motion.li key={item} initial={{
            opacity: 0,
            x: -10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: idx * 0.1
          }} className="flex items-start gap-3 text-midnight-navy/80 font-medium" data-id="element-224">
                <div className="mt-1 bg-accent-red rounded-full p-0.5" data-id="element-225">
                  <CheckIcon className="w-3 h-3 text-white" data-id="element-226" />
                </div>
                {item}
              </motion.li>)}
          </ul>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="bg-stark-white p-8 rounded-lg shadow-xl border border-gray-200" data-id="element-227">
          <div className="space-y-8" data-id="element-228">
            <div className="flex items-center justify-between border-b border-gray-100 pb-4" data-id="element-229">
              <h4 className="text-midnight-navy font-bold" data-id="element-230">
                Optimized Workflow
              </h4>
              <div className="flex gap-2" data-id="element-231">
                <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-232" />
                <div className="w-2 h-2 rounded-full bg-gray-200" data-id="element-233" />
              </div>
            </div>
            <div className="h-48 flex items-end gap-2 relative" data-id="element-234">
              {barData.map((bar, i) => <div key={i} className="flex-1 relative h-full flex items-end" onMouseEnter={() => setHoveredBar(i)} onMouseLeave={() => setHoveredBar(null)} data-id="element-235">
                  <AnimatePresence data-id="element-236">
                    {hoveredBar === i && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} className="absolute -top-12 left-1/2 -translate-x-1/2 bg-midnight-navy text-stark-white px-3 py-2 rounded-lg text-xs font-bold whitespace-nowrap shadow-xl z-10" data-id="element-237">
                        <div className="text-center" data-id="element-238">
                          <div className="text-accent-red" data-id="element-239">{bar.value}</div>
                          <div className="text-stark-white/60" data-id="element-240">{bar.label}</div>
                        </div>
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-midnight-navy rotate-45" data-id="element-241" />
                      </motion.div>}
                  </AnimatePresence>
                  <motion.div initial={{
                height: 0
              }} whileInView={{
                height: `${bar.height}%`
              }} viewport={{
                once: true
              }} transition={{
                duration: 1,
                delay: i * 0.05,
                ease: 'circOut'
              }} whileHover={{
                scale: 1.05,
                backgroundColor: '#E30B13'
              }} className="w-full bg-midnight-navy/5 rounded-t-sm cursor-pointer relative" style={{
                boxShadow: hoveredBar === i ? '0 0 20px rgba(227, 11, 19, 0.5)' : 'none'
              }} data-id="element-242">
                    {hoveredBar === i && <motion.div layoutId="activeBar" className="absolute inset-0 bg-accent-red rounded-t-sm" initial={false} transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 30
                }} data-id="element-243" />}
                  </motion.div>
                </div>)}
            </div>
            <div className="grid grid-cols-3 gap-4" data-id="element-244">
              {[{
              label: 'Efficiency',
              value: '+24%',
              color: 'text-midnight-navy'
            }, {
              label: 'Waste',
              value: '-18%',
              color: 'text-accent-red'
            }, {
              label: 'Margin',
              value: '+12%',
              color: 'text-midnight-navy'
            }].map((stat, idx) => <motion.div key={stat.label} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.5 + idx * 0.1
            }} className="p-4 bg-cool-gray rounded" data-id="element-245">
                  <div className="text-xs text-midnight-navy/50 uppercase font-bold mb-1" data-id="element-246">
                    {stat.label}
                  </div>
                  <div className={`text-xl font-bold ${stat.color}`} data-id="element-247">
                    {stat.value}
                  </div>
                </motion.div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}